import { ChangeDetectionStrategy, Component, DestroyRef, ViewChild, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { NgTemplateOutlet } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { DataTableHeader } from '@shared/tables/components/e-simple-data-table/e-simple-data-table.component';
import { EAdvancedTableComponent, TableRowDragStart, TableRowExpandedChange } from '@shared/tables/components/e-advanced-table/e-advanced-table.component';
import { FilterField, SortField } from '@common/querying/types';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { PlatformChartGroupDto } from '@data/_generated-api/platform-api/models/platform-chart-group-dto';
import { PlatformChartFacetsDto } from '@data/_generated-api/platform-api/models/platform-chart-facets-dto';
import { PlatformFacetCountDto } from '@data/_generated-api/platform-api/models/platform-facet-count-dto';
import { HdChartLibraryService } from './hd-chart-library.service';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdChartDataCache } from './hd-chart-data-cache.service';
import { RemoteChartChange } from '@common/chart-change-signals/chart-change-signals';
import { HdChartPropertyFilters, buildChartDataSourceQuery } from './hd-chart-datasource-query';
import { HdChartGateFilter, HdChartGateMatchMode } from './hd-chart-library.models';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HdChartDropOnGroup, HdChartGroupMove, HdChartGroupReorder, HdChartGroupTreeComponent } from './hd-chart-group-tree.component';
import { HD_CHART_DRAG_TYPE, buildIndentedGroupOptions } from './hd-chart-group-tree.util';
import { HdChartTrashDialogComponent } from './hd-chart-trash-dialog.component';
import { HdChartBirthdaysDialogComponent } from './hd-chart-birthdays-dialog.component';
import { isBirthdayWithinDays, nextBirthday } from './hd-chart-birthday.util';
import {
  HdChartGroupEditDialogComponent,
  HdChartGroupEditResult,
  HdChartGroupEditTarget,
} from './hd-chart-group-edit-dialog.component';
import {
  HdChartSetGroupsDialogComponent,
  HdChartSetGroupsResult,
  HdChartSetGroupsTarget,
} from './hd-chart-set-groups-dialog.component';
import { HdChartRelationsDialogComponent, HdChartRelationsTarget } from './hd-chart-relations-dialog.component';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { hdRelationTypeIcon, hdRelationTypeLabel } from './hd-chart-relation-metadata';
import { HdChartPropertyFilterComponent } from './hd-chart-property-filter.component';
import { HdChartCenterFilterComponent } from './hd-chart-center-filter.component';
import {
  buildHdAuthorityFilterOptions,
  buildHdDefinitionFilterOptions,
  buildHdProfileFilterOptions,
  buildHdTypeFilterOptions,
  withFacetCounts,
  buildHdGateFilterOptions,
  hdGateFilterSearch,
} from './hd-chart-property-filter-options';
import { HdChartCalculationResult, HdChartDocumentResult, HdChartUpdateInput, chartTypeForPreset } from './hd-chart-library.models';
import { HdChartCreateDialogComponent } from './hd-chart-create-dialog.component';
import { HdChartEditDialogComponent } from './hd-chart-edit-dialog.component';
import { HdChartShareDialogComponent } from './hd-chart-share-dialog.component';
import { HdChartSharedListDialogComponent } from './hd-chart-shared-list-dialog.component';
import { HdChartShareAccessService } from './hd-chart-share-access.service';
import { HdChartShareChange, HdChartShareTarget } from './hd-chart-share.models';
import { HdChartTypeTagComponent } from './hd-chart-type-tag.component';
import { HD_CHART_TYPE_ICONS } from './hd-chart-type-metadata';
import { HD_JUST_NOW_ICONS, HD_RAVE_NEW_YEAR_ICON } from './hd-chart-cycle-metadata';
import { HdRaveNewYearDialogComponent } from './hd-rave-new-year-dialog.component';
import { HdRaveNewYearRequest } from '../../transits/rave-new-year/hd-rave-new-year.service';
import { hdAuthorityLabel, hdDefinitionLabel, hdSummaryText, hdTypeLabel } from './hd-chart-property-labels';
import { HD_ENERGY_TYPE_ICONS } from './hd-chart-property-filter-options';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdBodygraphChartComponent } from '../bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartImportPanelComponent } from './import/hd-chart-import-panel.component';
import { ChartImportStage, ChartImportStateService } from './import/chart-import-state.service';
import { HdChartExportService } from './export/hd-chart-export.service';
import { CHART_EXPORT_FORMATS, ChartExportFormat } from './export/chart-export.models';
import { EExportDialogComponent, EExportDialogLabels, EExportFormatOption } from '@shared/components/dialogs/e-export-dialog/e-export-dialog.component';
import { NOTIFIER } from '@shared/notifier/types';
import { ViewportService } from '@core/services/viewport.service';

export type HdChartManagerMode = 'page' | 'dialog';

const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm';

/** Icoon per exportformaat in de exportdialoog. */
const CHART_EXPORT_ICONS: Record<ChartExportFormat, string> = {
  'mechanics-in-motion': 'file-code',
  'maia-online-csv': 'file-csv',
  'maia-desktop-mmi-v2': 'file-lines',
  'maia-desktop-mmi-v3': 'file-lines',
  'mybodygraph-mmi': 'file-lines',
};

/** Wat elk formaat bevat (sleutels onder humanDesign.chartManager.export.included). */
const CHART_EXPORT_INCLUDES: Record<ChartExportFormat, readonly string[]> = {
  'mechanics-in-motion': ['name', 'birthMoment', 'localTime', 'place', 'coordinates'],
  'maia-online-csv': ['name', 'gmtOffset', 'place'],
  'maia-desktop-mmi-v2': ['name', 'birthMoment', 'localTime', 'place'],
  'maia-desktop-mmi-v3': ['name', 'birthMoment', 'localTime', 'place'],
  'mybodygraph-mmi': ['name', 'birthMoment', 'localTime', 'place'],
};

/** Wat elk formaat bewust niet bevat (sleutels onder humanDesign.chartManager.export.excluded). */
const CHART_EXPORT_EXCLUDES: Record<ChartExportFormat, readonly string[]> = {
  'mechanics-in-motion': ['personal', 'derived'],
  'maia-online-csv': ['coordinates', 'personal', 'derived'],
  'maia-desktop-mmi-v2': ['coordinates', 'personal', 'derived'],
  'maia-desktop-mmi-v3': ['coordinates', 'personal', 'derived'],
  'mybodygraph-mmi': ['coordinates', 'personal', 'derived'],
};

@Component({
  selector: 'hd-chart-manager',
  standalone: true,
  imports: [
    SharedModule,
    // Expliciet naast SharedModule: de component-spec vervangt SharedModule
    // door stubs en de phone/desktop-templates delen via ngTemplateOutlet.
    NgTemplateOutlet,
    FormsModule,
    NgSelectModule,
    HdChartTrashDialogComponent,
    HdChartBirthdaysDialogComponent,
    HdRaveNewYearDialogComponent,
    HdBodygraphChartComponent,
    HdChartCreateDialogComponent,
    HdChartEditDialogComponent,
    HdChartShareDialogComponent,
    HdChartSharedListDialogComponent,
    HdChartTypeTagComponent,
    HdChartPropertyFilterComponent,
    HdChartCenterFilterComponent,
    HdChartGroupTreeComponent,
    HdChartGroupEditDialogComponent,
    HdChartSetGroupsDialogComponent,
    HdChartRelationsDialogComponent,
    HdChartImportPanelComponent,
    EExportDialogComponent,
    ESegmentedToggleComponent,
  ],
  templateUrl: './hd-chart-manager.component.html',
  styleUrl: './hd-chart-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartManagerComponent {
  private readonly library = inject(HdChartLibraryService);
  private readonly groupService = inject(HdChartGroupService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly notifier = inject(NOTIFIER);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly viewport = inject(ViewportService);
  /** Delen hangt aan de UPAM-feature: zonder feature geen deelknoppen of -iconen. */
  protected readonly shareAccess = inject(HdChartShareAccessService);

  readonly mode = input<HdChartManagerMode>('page');

  // ── Mobiel (docs/MOBIEL.md §6) ────────────────────────────────────────
  /** Phone = kaartenlijst i.p.v. de tabel; tap op de kaart opent de chart. */
  protected readonly isPhone = this.viewport.isPhone;
  /** Groepen + filters op phone: inklapbare sectie boven de lijst i.p.v. de zijbalk. */
  protected readonly phonePanelOpen = signal(false);
  /**
   * Onder desktopbreedte vallen de secundaire kolommen weg zodat de tabel op
   * een tablet past (in combinatie met de overflow-x-scroller van de tabel).
   */
  protected readonly hiddenColumns = computed<string[]>(() =>
    this.viewport.deviceClass() === 'tablet' || this.viewport.deviceClass() === 'phone'
      ? ['typeDescription', 'birthCountry', 'hdAuthority', 'hdDefinition']
      : [],
  );
  readonly loadChartRequested = output<{ id: string; preset: HdBodygraphChartPreset; type: PlatformChartType }>();
  /**
   * "Enkel berekenen" in de aanmaak-dialog (alleen dialog-modus): de host opent
   * de berekende chart als ONopgeslagen tab — de manager persisteert niets.
   */
  readonly calculatedChartRequested = output<HdChartCalculationResult>();
  /** Charts verwijderd (prullenbak): de host kan open tabs van deze charts sluiten. */
  readonly chartsDeleted = output<string[]>();
  /** Chart bewerkt (naam en/of herberekend document): de host kan open tabs van deze chart verversen. */
  readonly chartUpdated = output<string>();
  /** Snelle actie "Spelmodus" (enkel dialog-modus): de host opent de spelkeuze-dialog. */
  readonly gameRequested = output<void>();
  /** Rij-actie "Tijdlijn" → de levenstijdlijn-pagina van die chart (stap 5). */
  readonly timelineRequested = output<string>();
  /** Snelle actie "Just Now Transit" (dialog): de host laadt het huidige moment (personality-only) in een nieuwe tab. */
  readonly justNowRequested = output<void>();
  /** Snelle actie "Just Now Volledig" (dialog): de host laadt de volledige chart (design + personality) van nu. */
  readonly justNowFullRequested = output<void>();
  /** Iconen van de twee Just Now-varianten (dezelfde als in de Cycles-sectie en de shortcut-bar). */
  protected readonly justNowIcons = HD_JUST_NOW_ICONS;
  /** Snelle actie "Rave New Year" (dialog): jaar + variant gekozen, de host opent die chart in een nieuwe tab. */
  readonly raveNewYearRequested = output<HdRaveNewYearRequest>();
  protected readonly raveNewYearIcon = HD_RAVE_NEW_YEAR_ICON;
  protected readonly raveNewYearDialogOpen = signal(false);
  /** Snelle actie "Toon lege chart" (dialog): de host opent een verse lege tab en sluit de manager. */
  readonly emptyChartRequested = output<void>();

  readonly items = signal<PlatformChartListItemDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly totalRecordCount = signal(0);
  protected readonly page = signal(1);
  // 9 per pagina: met de twee filterregels past 10 nét niet meer zonder scrollbalk —
  // en 9 is voor een Human Design-product bovendien een passend getal.
  protected readonly pageSize = signal(9);
  /** Veelvouden van 9, in stijl. Query-cap is 100, dus 90 als maximum. */
  protected readonly pageSizeOptions = [9, 18, 27, 45, 90];
  protected readonly search = signal('');
  protected readonly sortFields = signal<SortField<PlatformChartListItemDto>[]>([]);
  protected readonly filterFields = signal<FilterField<PlatformChartListItemDto>[]>([]);
  protected readonly propertyFilters = signal<HdChartPropertyFilters>({});
  /**
   * Poortfilter (spec 2026-10-21-poortactivatie-zoektochten §4.5): server-side via
   * de queryparameters `gates`/`gateMatch`, apart van de datasource-filters (die
   * kennen enkel AND). Standaard En; de toggle verschijnt vanaf twee poorten.
   */
  protected readonly gateFilter = signal<HdChartGateFilter>({ gates: [], match: 'all' });
  protected readonly gateFilterSelected = computed(() => this.gateFilter().gates.map(gate => `${gate}`));
  protected readonly gateFilterSearch = hdGateFilterSearch;
  protected searchModel = '';
  protected readonly createDialogOpen = signal(false);
  protected readonly importDialogOpen = signal(false);

  // ── Import uit andere HD-software (docs/human-design/hd-chart-import.md) ──
  private readonly importState = inject(ChartImportStateService);
  /** Vorige import-stage om de overgang running → klaar te herkennen. */
  private importStageWas: ChartImportStage = 'idle';
  /**
   * De importrun draait in een root-service en loopt door wanneer deze dialog
   * (of de hele manager) gesloten is; zodra hij afrondt terwijl de manager
   * open staat, moeten lijst + facetten de nieuwe charts tonen.
   */
  private readonly importFinishedRefresh = effect(() => {
    const stage = this.importState.stage();
    const wasRunning = this.importStageWas === 'running';
    this.importStageWas = stage;
    if (wasRunning && (stage === 'done' || stage === 'cancelled')) {
      void this.reload();
      void this.reloadFacets();
      // Ook de groepenboom: de import kan charts in een doelgroep geplaatst
      // hebben en de aantallen naast de groepsnamen komen uit listGroups().
      void this.reloadGroups();
    }
  });
  /** Geladen document van de chart die in de edit-dialog openstaat; null = gesloten. */
  protected readonly editTarget = signal<HdChartDocumentResult | null>(null);
  /** "My chart"-status van die chart (uit de lijst-DTO; het document draagt hem niet). */
  protected readonly editTargetIsMyChart = signal(false);
  /** Chart in de deeldialoog (spec 2026-10-20-chart-share); null = gesloten. */
  protected readonly shareTarget = signal<HdChartShareTarget | null>(null);
  /** Overzicht "Gedeelde charts" (snelle actie). */
  protected readonly sharedDialogOpen = signal(false);
  /** Detail per uitgeklapte rij (lazy), keyed op chart-id. */
  protected readonly details = signal<Record<string, HdChartDocumentResult>>({});

  /**
   * Live seintjes van andere toestellen/browsertabs (spec chart-datacache 2026-10-06 §4.2): lijst en
   * facetten gedebounced herladen (een batch-actie elders stuurt meerdere seintjes kort na elkaar),
   * en de detail-pane/relatiechips van al uitgeklapte rijen meteen verversen.
   */
  private remoteReloadTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly remoteChangesSubscription = inject(HdChartDataCache).remoteChanges$
    .pipe(takeUntilDestroyed())
    .subscribe(change => this.onRemoteChartChange(change));
  private readonly clearRemoteReloadTimer = inject(DestroyRef).onDestroy(() => clearTimeout(this.remoteReloadTimer));

  // ── Groepen (fase 4) ──────────────────────────────────────────────────
  protected readonly groups = signal<PlatformChartGroupDto[]>([]);
  /** null = "Alle charts" (geen groepsfilter). */
  protected readonly selectedGroupId = signal<string | null>(null);
  protected readonly groupEditTarget = signal<HdChartGroupEditTarget | null>(null);
  protected readonly setGroupsTarget = signal<HdChartSetGroupsTarget | null>(null);
  /** Selectie van de groepen-instellen-dialog op het moment van "Nieuwe groep"; null = geen lopende aanvraag. */
  private pendingSetGroupsSelection: string[] | null = null;
  /** Foutmelding van de laatste groepsactie (bv. dubbele naam); leeg = geen. */
  protected readonly groupError = signal('');
  /** Groepsnamen per chart voor de detail-pane (lazy bij uitklappen). */
  protected readonly groupNamesByChart = signal<Record<string, string[]>>({});

  // ── Relaties (fase 5) ─────────────────────────────────────────────────
  protected readonly relationsTarget = signal<HdChartRelationsTarget | null>(null);
  /** Relaties per chart voor de detail-pane-chips (lazy bij uitklappen). */
  protected readonly relationsByChart = signal<Record<string, PlatformChartRelationDto[]>>({});

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      newChart: translate('humanDesign.chartManager.newChart'),
      load: translate('humanDesign.chartManager.load'),
      edit: translate('humanDesign.chartManager.edit'),
      share: translate('humanDesign.chartManager.share'),
      sharedTooltip: translate('humanDesign.chartManager.sharedTooltip'),
      delete: translate('humanDesign.chartManager.delete'),
      description: translate('humanDesign.chartManager.detail.description'),
      hdSummary: translate('humanDesign.chartManager.detail.hdSummary'),
      backfillAction: translate('humanDesign.chartManager.backfill.action'),
      importAction: translate('humanDesign.chartManager.import.action'),
      importTitle: translate('humanDesign.chartManager.import.title'),
      exportAction: translate('humanDesign.chartManager.export.action'),
      exportSelectionAction: translate('humanDesign.chartManager.export.selectionAction'),
      clearFilters: translate('humanDesign.chartManager.clearFilters'),
      gateFilter: translate('humanDesign.chartManager.gateFilter'),
      filterApplyHint: translate('humanDesign.chartManager.filterApplyHint'),
      gateMatchAll: translate('humanDesign.chartManager.gateMatchAll'),
      gateMatchAny: translate('humanDesign.chartManager.gateMatchAny'),
      gateMatchAllTooltip: translate('humanDesign.chartManager.gateMatchAllTooltip'),
      gateMatchAnyTooltip: translate('humanDesign.chartManager.gateMatchAnyTooltip'),
      groupsTitle: translate('humanDesign.chartManager.groups.title'),
      newGroup: translate('humanDesign.chartManager.groups.newGroup'),
      setGroups: translate('humanDesign.chartManager.groups.setGroups'),
      quickActionsTitle: translate('humanDesign.chartManager.quickActions.title'),
      loadMyChart: translate('humanDesign.chartManager.quickActions.loadMyChart'),
      noMyChart: translate('humanDesign.chartManager.quickActions.noMyChart'),
      gameAction: translate('humanDesign.chartManager.quickActions.game'),
      justNowAction: translate('humanDesign.chartManager.quickActions.justNow'),
      justNowTooltip: translate('humanDesign.chartManager.quickActions.justNowTooltip'),
      justNowFullAction: translate('humanDesign.chartManager.quickActions.justNowFull'),
      justNowFullTooltip: translate('humanDesign.chartManager.quickActions.justNowFullTooltip'),
      raveNewYearAction: translate('humanDesign.chartManager.quickActions.raveNewYear'),
      raveNewYearTooltip: translate('humanDesign.chartManager.quickActions.raveNewYearTooltip'),
      emptyChartAction: translate('humanDesign.chartManager.quickActions.emptyChart'),
      trashAction: translate('humanDesign.chartManager.trash.action'),
      birthdaysAction: translate('humanDesign.chartManager.birthdays.action'),
      sharedChartsAction: translate('humanDesign.chartManager.sharedCharts.action'),
      compositionTitle: translate('humanDesign.chartManager.composition.title'),
      batchGroupPlaceholder: translate('humanDesign.chartManager.batch.groupPlaceholder'),
      batchAdd: translate('humanDesign.chartManager.batch.add'),
      batchMove: translate('humanDesign.chartManager.batch.move'),
      batchMoveToTrash: translate('humanDesign.chartManager.batch.moveToTrash'),
      batchClearSelection: translate('humanDesign.chartManager.batch.clearSelection'),
      markMyChart: translate('humanDesign.chartManager.myChart.mark'),
      unmarkMyChart: translate('humanDesign.chartManager.myChart.unmark'),
      timeline: translate('humanDesign.chartManager.detail.timeline'),
      detailGroups: translate('humanDesign.chartManager.detail.groups'),
      relationsTitle: translate('humanDesign.chartManager.relations.title'),
      detailRelations: translate('humanDesign.chartManager.detail.relations'),
      created: translate('humanDesign.chartManager.detail.created'),
      modified: translate('humanDesign.chartManager.detail.modified'),
      timezone: translate('humanDesign.chartManager.detail.timezone'),
      latLon: translate('humanDesign.chartManager.detail.latLon'),
      source: translate('humanDesign.chartManager.detail.source'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      propertyType: translate('humanDesign.properties.type'),
      propertyAuthority: translate('humanDesign.properties.authority'),
      propertyDefinition: translate('humanDesign.properties.definition'),
      propertyProfile: translate('humanDesign.properties.profile'),
      phoneFiltersToggle: translate('humanDesign.chartManager.phone.filtersToggle'),
      phoneActions: translate('humanDesign.chartManager.phone.actions'),
      phoneEmpty: translate('humanDesign.chartManager.phone.empty'),
    };
  });

  /** Placeholder benoemt expliciet de zoekscope: de geselecteerde groep of alle charts. */
  protected readonly searchPlaceholder = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const groupId = this.selectedGroupId();
    const group = groupId ? this.groups().find(candidate => candidate.id === groupId) : undefined;
    return group
      ? this.transloco.translate<string>('humanDesign.chartManager.searchPlaceholderInGroup', { name: group.name }, language)
      : this.transloco.translate<string>('humanDesign.chartManager.searchPlaceholderAllCharts', undefined, language);
  });

  /** Facetten (verdeling binnen de actieve groep-scope); null zolang niet geladen. */
  protected readonly facets = signal<PlatformChartFacetsDto | null>(null);

  /** Facetten zijn decoratief: fouten stil negeren, de filterbalk blijft gewoon werken. */
  protected async reloadFacets(): Promise<void> {
    try {
      this.facets.set(await this.groupService.getFacets(this.selectedGroupId() ?? undefined));
    } catch {
      this.facets.set(null);
    }
  }

  /** Optielijsten voor de eigenschappen-filterbalk (taalbewust, met facet-counts wanneer geladen). */
  protected readonly propertyFilterOptions = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    const facets = this.facets();
    return {
      types: withFacetCounts(buildHdTypeFilterOptions(translate), facets?.types),
      authorities: withFacetCounts(buildHdAuthorityFilterOptions(translate), facets?.authorities),
      definitions: withFacetCounts(buildHdDefinitionFilterOptions(translate), facets?.definitions),
      profiles: withFacetCounts(buildHdProfileFilterOptions(), facets?.profiles),
      gates: buildHdGateFilterOptions(gate => this.gateInfo.gateInfoIn(gate, language).translations.keynote1),
    };
  });

  /** En/Of tussen de gekozen poorten (gedeelde e-segmented-toggle). */
  protected readonly gateMatchOptions = computed<readonly ESegmentedToggleOption<HdChartGateMatchMode>[]>(() => {
    const text = this.text();
    return [
      { value: 'all', label: text.gateMatchAll, title: text.gateMatchAllTooltip },
      { value: 'any', label: text.gateMatchAny, title: text.gateMatchAnyTooltip },
    ];
  });

  protected onPropertyFilterChange(listKey: keyof HdChartPropertyFilters, values: string[]): void {
    this.propertyFilters.update(filters => ({ ...filters, [listKey]: values }));
    this.page.set(1);
    void this.reload();
  }

  protected onCenterFilterChange(centerStates: Record<string, number>): void {
    this.propertyFilters.update(filters => ({ ...filters, centerStates }));
    this.page.set(1);
    void this.reload();
  }

  protected onGateFilterChange(values: string[]): void {
    const gates = values.map(value => Number(value)).filter(gate => Number.isInteger(gate) && gate >= 1 && gate <= 64);
    this.gateFilter.update(filter => ({ ...filter, gates }));
    this.page.set(1);
    void this.reload();
  }

  protected onGateMatchChange(match: HdChartGateMatchMode): void {
    this.gateFilter.update(filter => ({ ...filter, match }));
    this.page.set(1);
    void this.reload();
  }

  /** Alle eigenschappen-filters in één keer wissen (no-op wanneer er niets actief is). */
  protected clearPropertyFilters(): void {
    const filters = this.propertyFilters();
    const active =
      (filters.hdTypes?.length ?? 0) > 0 ||
      (filters.hdAuthorities?.length ?? 0) > 0 ||
      (filters.hdDefinitions?.length ?? 0) > 0 ||
      (filters.hdProfiles?.length ?? 0) > 0 ||
      Object.keys(filters.centerStates ?? {}).length > 0 ||
      this.gateFilter().gates.length > 0;
    if (!active) return;
    this.propertyFilters.set({});
    this.gateFilter.set({ gates: [], match: 'all' });
    this.page.set(1);
    void this.reload();
  }

  readonly headers = computed<DataTableHeader<PlatformChartListItemDto>[]>(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return [
      {
        key: 'name',
        label: translate('humanDesign.chartManager.columns.name'),
        sortable: true,
        filter: 'text',
      },
      {
        key: 'typeDescription',
        label: translate('humanDesign.chartManager.columns.type'),
        sortable: true,
        // Filter via de eTableColumnFilter-template in de html (e-enum-select met iconen).
      },
      {
        key: 'birthDateUtc',
        label: translate('humanDesign.chartManager.columns.birthDate'),
        sortable: true,
        width: 150,
      },
      {
        key: 'birthCity',
        label: translate('humanDesign.chartManager.columns.city'),
        sortable: true,
      },
      {
        key: 'birthCountry',
        label: translate('humanDesign.chartManager.columns.country'),
        sortable: true,
        filter: 'text',
        width: 110,
      },
      // Gepromoveerde HD-eigenschappen: zichtbaar naast de filterbalk, zodat een
      // filterresultaat ("alle generators") ook in de rijen af te lezen is.
      {
        key: 'hdType',
        label: translate('humanDesign.chartManager.columns.hdType'),
        sortable: true,
        width: 150,
      },
      {
        key: 'hdAuthority',
        label: translate('humanDesign.properties.authority'),
        sortable: true,
        width: 110,
      },
      {
        key: 'hdDefinition',
        label: translate('humanDesign.properties.definition'),
        sortable: true,
        width: 130,
      },
      {
        key: 'hdProfile',
        label: translate('humanDesign.properties.profile'),
        sortable: true,
        width: 70,
      },
      { key: 'actions', label: '', width: 230 },
    ];
  });

  ngOnInit(): void {
    void this.init();
  }

  /** Eerst de groepen: een eventuele default-groep bepaalt het filter van de eerste chart-load. */
  private async init(): Promise<void> {
    try {
      await this.reloadGroups();
      const defaultGroup = this.groups().find(group => group.isDefaultSelection);
      if (defaultGroup?.id) this.selectedGroupId.set(defaultGroup.id);
    } catch {
      // Groepen laden faalde: dan gewoon alle charts tonen.
    }
    void this.reloadFacets();
    await this.reload();
  }

  async reload(): Promise<void> {
    this.loading.set(true);
    try {
      const result = await this.library.queryCharts(
        buildChartDataSourceQuery({
          page: this.page(),
          pageSize: this.pageSize(),
          search: this.search(),
          sortFields: this.sortFields(),
          filterFields: this.filterFields(),
          propertyFilters: this.propertyFilters(),
        }),
        this.selectedGroupId() ?? undefined,
        this.gateFilter(),
      );
      this.items.set(result.items);
      this.totalRecordCount.set(result.totalRecordCount);
    } finally {
      this.loading.set(false);
    }
  }

  // ── Groepen (fase 4) ──────────────────────────────────────────────────
  protected async reloadGroups(): Promise<void> {
    this.groups.set(await this.groupService.listGroups());
  }

  protected onGroupSelected(groupId: string | null): void {
    this.selectedGroupId.set(groupId);
    this.page.set(1);
    void this.reload();
    void this.reloadFacets();
    // Phone: na een groepskeuze het paneel sluiten zodat het resultaat zichtbaar is.
    if (this.isPhone()) this.phonePanelOpen.set(false);
  }

  protected openCreateGroup(): void {
    this.groupError.set('');
    this.pendingSetGroupsSelection = null;
    this.groupEditTarget.set({ group: null, parentGroupId: this.selectedGroupId() });
  }

  protected openEditGroup(group: PlatformChartGroupDto): void {
    this.groupError.set('');
    this.pendingSetGroupsSelection = null;
    this.groupEditTarget.set({ group, parentGroupId: null });
  }

  /**
   * "Nieuwe groep" vanuit de groepen-instellen-dialog: de groep-toevoegen-dialog
   * opent erbovenop (top-level); na het aanmaken komt de nieuwe groep in de
   * dropdown én in de selectie, naast wat de gebruiker al gekozen had.
   */
  protected onSetGroupsCreateGroupRequested(currentSelection: string[]): void {
    this.groupError.set('');
    this.pendingSetGroupsSelection = currentSelection;
    this.groupEditTarget.set({ group: null, parentGroupId: null });
  }

  protected async onGroupSaved(result: HdChartGroupEditResult): Promise<void> {
    const editing = this.groupEditTarget()?.group ?? null;
    this.groupError.set('');
    try {
      let createdId: string | null = null;
      if (editing?.id) {
        await this.groupService.updateGroup(editing.id, result.name, result.parentGroupId, result.isDefaultSelection, result.icon ?? null);
      } else {
        const created = await this.groupService.createGroup(result.name, result.parentGroupId, result.isDefaultSelection, result.icon ?? null);
        createdId = created?.id ?? null;
      }
      // Eerst de lijst verversen, zodat de nieuwe groep als optie bestaat vóór hij geselecteerd wordt.
      await this.reloadGroups();
      this.selectCreatedGroupInSetGroupsDialog(createdId);
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Herorden-drop in de boom: eerst een eventuele parent-wissel, dan de volgorde persisteren. */
  protected async onGroupsReordered(payload: HdChartGroupReorder): Promise<void> {
    this.groupError.set('');
    try {
      const reparent = payload.reparent;
      if (reparent?.group.id) {
        await this.groupService.updateGroup(
          reparent.group.id, reparent.group.name ?? '', reparent.newParentId,
          reparent.group.isDefaultSelection ?? false, reparent.group.icon ?? null);
      }
      await this.groupService.reorderGroups(payload.orderedGroupIds);
      await this.reloadGroups();
      // Bij een parent-wissel kan de subtree-scope van het actieve filter wijzigen.
      if (reparent && this.selectedGroupId()) await this.reload();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  protected async deleteGroup(group: PlatformChartGroupDto): Promise<void> {
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.groups.deleteConfirm',
      { name: group.name },
      this.contentLang.effectiveLang(),
    );
    const ok = await this.confirmer.confirm(message, { color: 'danger' });
    if (!ok || !group.id) return;

    this.groupError.set('');
    try {
      await this.groupService.deleteGroup(group.id);
      await this.reloadGroups();
      // Stond het filter op (een subgroep van) de verwijderde groep, dan terug naar "Alle charts".
      if (this.selectedGroupId() && !this.groups().some(g => g.id === this.selectedGroupId())) {
        this.onGroupSelected(null);
      }
      this.groupNamesByChart.set({});
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Chart-rij wordt gesleept: stippellijn-affordance rond de groepenboom. */
  protected readonly chartDragActive = signal(false);
  /** Groep die kort oplicht als succes-cue na een drop. */
  protected readonly flashGroupId = signal<string | null>(null);
  private flashTimer: ReturnType<typeof setTimeout> | undefined;

  /** Tabelrij als dragbron markeren zodat de groepenboom hem als chart herkent. */
  protected onChartRowDragStart({ item, event }: TableRowDragStart<PlatformChartListItemDto>): void {
    if (!item.id) return;
    event.dataTransfer?.setData(HD_CHART_DRAG_TYPE, item.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
    this.chartDragActive.set(true);
  }

  protected onChartRowDragEnd(): void {
    this.chartDragActive.set(false);
  }

  /** Chart op een groep gedropt: lidmaatschap toevoegen (idempotent — al lid = niets doen). */
  protected async onChartDroppedOnGroup(drop: HdChartDropOnGroup): Promise<void> {
    this.groupError.set('');
    try {
      const currentGroupIds = await this.groupService.getChartGroupIds(drop.chartId);
      if (currentGroupIds.includes(drop.groupId)) return;
      await this.groupService.setChartGroups(drop.chartId, [...currentGroupIds, drop.groupId]);
      this.groupNamesByChart.update(map => {
        const rest = { ...map };
        delete rest[drop.chartId];
        return rest;
      });
      await this.reloadGroups();
      void this.reloadFacets();
      this.flashGroup(drop.groupId);
      if (this.selectedGroupId()) await this.reload();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  private flashGroup(groupId: string): void {
    clearTimeout(this.flashTimer);
    this.flashGroupId.set(groupId);
    this.flashTimer = setTimeout(() => this.flashGroupId.set(null), 900);
  }

  /** Drag & drop in de boom: zelfde update als de edit-dialog, alleen de parent wijzigt. */
  protected async onGroupMoved(move: HdChartGroupMove): Promise<void> {
    if (!move.group.id) return;
    this.groupError.set('');
    try {
      await this.groupService.updateGroup(
        move.group.id, move.group.name ?? '', move.newParentId,
        move.group.isDefaultSelection ?? false, move.group.icon ?? null);
      await this.reloadGroups();
      // Bij een actief groepsfilter kan de subtree-scope (en dus het resultaat) wijzigen.
      if (this.selectedGroupId()) await this.reload();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  private selectCreatedGroupInSetGroupsDialog(createdId: string | null): void {
    const selection = this.pendingSetGroupsSelection;
    this.pendingSetGroupsSelection = null;
    if (createdId == null || selection == null) return;
    this.setGroupsTarget.update(target => target
      ? { ...target, selectedGroupIds: [...selection.filter(id => id !== createdId), createdId] }
      : target);
  }

  protected async openSetGroups(item: PlatformChartListItemDto): Promise<void> {
    if (!item.id) return;
    const selectedGroupIds = await this.groupService.getChartGroupIds(item.id);
    this.setGroupsTarget.set({ chartId: item.id, chartName: item.name ?? '', selectedGroupIds });
  }

  protected async onSetGroupsSaved(result: HdChartSetGroupsResult): Promise<void> {
    this.groupError.set('');
    try {
      await this.groupService.setChartGroups(result.chartId, result.groupIds);
      this.groupNamesByChart.update(map => {
        const rest = { ...map };
        delete rest[result.chartId];
        return rest;
      });
      await this.reloadGroups();
      void this.reloadFacets();
      // Bij een actief groepsfilter kan de chart in of uit het resultaat vallen.
      if (this.selectedGroupId()) await this.reload();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Groepsnamen voor de detail-pane; '—' zolang niet geladen of leeg. */
  protected groupNames(item: PlatformChartListItemDto): string {
    const names = item.id ? this.groupNamesByChart()[item.id] : undefined;
    return names?.length ? names.join(', ') : '—';
  }

  // ── Rijselectie + batch-toewijzen (R2-D) ──────────────────────────────
  @ViewChild(EAdvancedTableComponent) private table?: EAdvancedTableComponent<PlatformChartListItemDto>;

  /** Geselecteerde chart-ids (conventie: ids, geen object-referenties). */
  protected readonly selectedChartIds = signal<string[]>([]);
  protected readonly batchGroupId = signal<string | null>(null);

  /** Ingesprongen groepskeuze voor de batch-balk. */
  protected readonly batchGroupOptions = computed(() => buildIndentedGroupOptions(this.groups()));

  /** Expliciete actiekeuze van de gebruiker; null = automatisch (verplaatsen binnen een groep). */
  protected readonly batchGroupActionChoice = signal<'move' | 'add' | null>(null);

  /** Effectieve batch-actie: "verplaatsen" bestaat alleen mét een actieve groepsselectie. */
  protected readonly batchGroupAction = computed<'move' | 'add'>(() => {
    if (!this.selectedGroupId()) return 'add';
    return this.batchGroupActionChoice() ?? 'move';
  });

  protected readonly batchGroupActionOptions = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    const options = [{ value: 'add', label: translate('humanDesign.chartManager.batch.actionAdd') }];
    if (this.selectedGroupId()) {
      options.unshift({ value: 'move', label: translate('humanDesign.chartManager.batch.actionMove') });
    }
    return options;
  });

  protected readonly batchApplyText = computed(() =>
    this.batchGroupAction() === 'move' ? this.text().batchMove : this.text().batchAdd,
  );

  protected get batchApplyIcon(): string {
    return this.batchGroupAction() === 'move' ? 'arrow-right-arrow-left' : 'folder-plus';
  }

  /** Aantalstekst voor de batch-balk (met interpolatie, dus buiten text()). */
  protected readonly batchSelectedText = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate<string>(
      'humanDesign.chartManager.batch.selectedCount',
      { count: this.selectedChartIds().length },
      this.contentLang.effectiveLang(),
    );
  });

  protected onSelectionChange(items: PlatformChartListItemDto[]): void {
    this.selectedChartIds.set(items.map(item => item.id).filter((id): id is string => !!id));
  }

  protected clearSelection(): void {
    this.table?.clearSelectedItems();
    this.selectedChartIds.set([]);
  }

  /** Batch: selectie toevoegen aan (idempotent server-side) of verplaatsen naar de gekozen groep. */
  protected async applyBatchGroupAction(): Promise<void> {
    const targetGroupId = this.batchGroupId();
    const chartIds = this.selectedChartIds();
    if (!targetGroupId || chartIds.length === 0) return;
    const sourceGroupId = this.selectedGroupId();
    this.groupError.set('');
    try {
      if (this.batchGroupAction() === 'move' && sourceGroupId) {
        await this.moveChartsBetweenGroups(chartIds, sourceGroupId, targetGroupId);
      } else {
        await this.groupService.addChartsToGroup(targetGroupId, chartIds);
      }
      this.groupNamesByChart.update(map => {
        const rest = { ...map };
        for (const chartId of chartIds) delete rest[chartId];
        return rest;
      });
      await this.reloadGroups();
      void this.reloadFacets();
      this.flashGroup(targetGroupId);
      this.clearSelection();
      this.batchGroupId.set(null);
      if (this.selectedGroupId()) await this.reload();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /**
   * Verplaatsen = toevoegen aan de doelgroep + alleen het lidmaatschap van de
   * bron-groep weghalen; lidmaatschappen van andere groepen blijven ongemoeid.
   */
  private async moveChartsBetweenGroups(chartIds: string[], fromGroupId: string, toGroupId: string): Promise<void> {
    for (const chartId of chartIds) {
      const currentGroupIds = await this.groupService.getChartGroupIds(chartId);
      const newGroupIds = [...new Set([...currentGroupIds.filter(id => id !== fromGroupId), toGroupId])];
      const unchanged =
        newGroupIds.length === currentGroupIds.length && newGroupIds.every(id => currentGroupIds.includes(id));
      if (unchanged) continue;
      await this.groupService.setChartGroups(chartId, newGroupIds);
    }
  }

  /** Knoplabel "Uit {groep} verwijderen"; leeg zonder actieve groepsselectie (knop verborgen). */
  protected readonly batchRemoveFromGroupText = computed(() => {
    const groupId = this.selectedGroupId();
    const group = groupId ? this.groups().find(candidate => candidate.id === groupId) : undefined;
    if (!group) return '';
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate<string>(
      'humanDesign.chartManager.batch.removeFromGroup',
      { name: group.name },
      this.contentLang.effectiveLang(),
    );
  });

  /**
   * Batch: selectie uit de actieve groep halen. Alleen het lidmaatschap van exact
   * deze groep verdwijnt — de charts zelf en eventuele subgroep-lidmaatschappen
   * blijven staan (de groepsscope toont ook subgroep-charts; die blijven dus zichtbaar).
   */
  protected async removeSelectionFromGroup(): Promise<void> {
    const groupId = this.selectedGroupId();
    const chartIds = this.selectedChartIds();
    if (!groupId || chartIds.length === 0) return;
    this.groupError.set('');
    try {
      for (const chartId of chartIds) {
        const currentGroupIds = await this.groupService.getChartGroupIds(chartId);
        if (!currentGroupIds.includes(groupId)) continue;
        await this.groupService.setChartGroups(chartId, currentGroupIds.filter(id => id !== groupId));
      }
      this.groupNamesByChart.update(map => {
        const rest = { ...map };
        for (const chartId of chartIds) delete rest[chartId];
        return rest;
      });
      this.clearSelection();
      await this.reload();
      void this.reloadGroups();
      void this.reloadFacets();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Batch: selectie naar de prullenbak (soft-delete) na bevestiging. */
  protected async deleteSelection(): Promise<void> {
    const chartIds = this.selectedChartIds();
    if (chartIds.length === 0) return;

    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.batch.trashConfirm',
      { count: chartIds.length },
      this.contentLang.effectiveLang(),
    );
    if (!(await this.confirmer.confirm(message, { color: 'danger' }))) return;

    this.groupError.set('');
    try {
      await this.groupService.deleteCharts(chartIds);
      this.chartsDeleted.emit(chartIds);
      this.clearSelection();
      await this.reload();
      void this.reloadGroups();
      void this.reloadFacets();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  // ── Export (docs/human-design/hd-chart-export.md) ──────────────────────
  private readonly exporter = inject(HdChartExportService);
  /** Wat de open exportdialoog exporteert: de huidige lijst of de selectie; undefined = dicht. */
  protected readonly exportScope = signal<'list' | 'selection' | undefined>(undefined);
  protected readonly exporting = signal(false);

  protected readonly exportLabels = computed<EExportDialogLabels>(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(`humanDesign.chartManager.export.${key}`, undefined, language);
    return { includes: translate('includes'), excludes: translate('excludes'), download: translate('download') };
  });

  protected readonly exportTitle = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate<string>('humanDesign.chartManager.export.title', undefined, this.contentLang.effectiveLang());
  });

  protected readonly exportIntro = computed(() => {
    const scope = this.exportScope();
    if (!scope) return '';
    this.contentLang.translationsLoadedTick();
    const count = scope === 'selection' ? this.selectedChartIds().length : this.totalRecordCount();
    return this.transloco.translate<string>(
      scope === 'selection' ? 'humanDesign.chartManager.export.introSelection' : 'humanDesign.chartManager.export.introList',
      { count },
      this.contentLang.effectiveLang(),
    );
  });

  /** Per formaat wat erin zit en wat niet; de volgorde van de dialoog = CHART_EXPORT_FORMATS. */
  protected readonly exportOptions = computed<readonly EExportFormatOption[]>(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(`humanDesign.chartManager.export.${key}`, undefined, language);
    return CHART_EXPORT_FORMATS.map(format => ({
      id: format,
      icon: CHART_EXPORT_ICONS[format],
      title: translate(`formats.${format}.title`),
      description: translate(`formats.${format}.description`),
      includes: CHART_EXPORT_INCLUDES[format].map(key => translate(`included.${key}`)),
      excludes: CHART_EXPORT_EXCLUDES[format].map(key => translate(`excluded.${key}`)),
      note: translate(`formats.${format}.note`),
    }));
  });

  protected openExport(scope: 'list' | 'selection'): void {
    this.exportScope.set(scope);
  }

  protected onExportOpenChange(open: boolean): void {
    if (!open && !this.exporting()) this.exportScope.set(undefined);
  }

  protected async runExport(formatId: string): Promise<void> {
    const scope = this.exportScope();
    const format = CHART_EXPORT_FORMATS.find(candidate => candidate === formatId);
    if (!scope || !format || this.exporting()) return;
    const language = this.contentLang.effectiveLang();
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(`humanDesign.chartManager.export.${key}`, params, language);

    this.exporting.set(true);
    try {
      const chartIds = scope === 'selection' ? this.selectedChartIds() : await this.currentListChartIds();
      const outcome = await this.exporter.exportCharts(chartIds, format);
      if (outcome.exported === 0) {
        this.notifier.warning(translate('nothing'));
        return;
      }
      this.notifier.success(translate(outcome.skipped > 0 ? 'doneWithSkipped' : 'done', { ...outcome }));
      this.exportScope.set(undefined);
    } catch {
      // De dialoog blijft open: opnieuw proberen of een ander formaat kiezen kan meteen.
      this.notifier.error(translate('error'));
    } finally {
      this.exporting.set(false);
    }
  }

  /**
   * Alle chart-ids van de huidige lijst (groep, zoekterm, filters, poortfilter), over alle
   * pagina's heen. Niet op type gefilterd: de backend laat niet-standaard charts weg en telt ze
   * zo mee als "overgeslagen".
   */
  private async currentListChartIds(): Promise<string[]> {
    const items = await this.library.queryAllCharts(
      buildChartDataSourceQuery({
        page: 1,
        pageSize: 100,
        search: this.search(),
        sortFields: this.sortFields(),
        filterFields: this.filterFields(),
        propertyFilters: this.propertyFilters(),
      }),
      this.selectedGroupId() ?? undefined,
      this.gateFilter(),
    );
    return items.map(item => item.id).filter((id): id is string => !!id);
  }

  // ── Prullenbak (R2-C) ─────────────────────────────────────────────────
  protected readonly trashDialogOpen = signal(false);

  /** Na een restore verschijnt de chart weer in de lijst (en tellers kunnen wijzigen). */
  protected onChartRestored(): void {
    void this.reload();
    void this.reloadGroups();
    void this.reloadFacets();
  }

  // ── Samenstelling (R2-E) ──────────────────────────────────────────────
  protected readonly compositionOpen = signal(true);

  /** Type-verdeling met mini-bars + compacte tekstregels voor autoriteit/profiel. */
  protected readonly composition = computed(() => {
    const facets = this.facets();
    if (!facets || !facets.totalCount) return null;
    const translate = this.contentTranslate();
    const max = Math.max(1, ...(facets.types ?? []).map(facet => facet.count ?? 0));
    return {
      total: facets.totalCount,
      types: (facets.types ?? []).map(facet => ({
        icon: facet.key ? HD_ENERGY_TYPE_ICONS[facet.key] ?? null : null,
        label: hdTypeLabel(translate, facet.key) ?? facet.key ?? '—',
        count: facet.count ?? 0,
        pct: Math.round(((facet.count ?? 0) / max) * 100),
      })),
      authorities: this.facetLine(facets.authorities, key => hdAuthorityLabel(translate, key)),
      profiles: this.facetLine(facets.profiles, key => key),
    };
  });

  /** "Label n · Label n · …" — compacte één-regel-samenvatting van een facetlijst. */
  private facetLine(
    facets: PlatformFacetCountDto[] | null | undefined,
    toLabel: (key: string | null | undefined) => string | null | undefined,
  ): string {
    return (facets ?? [])
      .map(facet => `${toLabel(facet.key) ?? facet.key ?? '—'} ${facet.count ?? 0}`)
      .join(' · ');
  }

  // ── Verjaardagen (R2-G) ───────────────────────────────────────────────
  protected readonly birthdaysDialogOpen = signal(false);

  protected isBirthdaySoon(item: PlatformChartListItemDto): boolean {
    // Zelfde vooruitvenster als de verjaardagen-dialog (vandaag = dag 4 van 7).
    return isBirthdayWithinDays(item.birthDateLocal ?? item.birthDateUtc, 3);
  }

  protected birthdayTooltip(item: PlatformChartListItemDto): string {
    const upcoming = nextBirthday(item.birthDateLocal ?? item.birthDateUtc);
    if (!upcoming) return '';
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate<string>(
      'humanDesign.chartManager.birthdays.rowTooltip',
      { date: moment(upcoming.date).format('DD-MM'), age: upcoming.turnsAge },
      this.contentLang.effectiveLang(),
    );
  }

  // ── My chart + snelle acties ──────────────────────────────────────────
  /** Foutmelding/lege-staat van de snelle acties in de zijbalk; leeg = geen. */
  protected readonly quickActionMessage = signal('');

  /** Ster-toggle in de rij: markering is uniek per gebruiker (server unset de vorige). */
  protected async toggleMyChart(item: PlatformChartListItemDto): Promise<void> {
    if (!item.id) return;
    this.groupError.set('');
    try {
      await this.groupService.setMyChart(item.id, !item.isMyChart);
      await this.reload();
    } catch (e) {
      this.groupError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Rij-actie "Delen" (spec 2026-10-20-chart-share): opent de deeldialoog voor deze chart. */
  protected openShare(item: PlatformChartListItemDto): void {
    if (!item.id) return;
    this.shareTarget.set({
      id: item.id,
      name: item.name ?? '',
      type: item.type ?? PlatformChartType.Standard,
      shareToken: item.shareToken ?? null,
    });
  }

  /** Deelstatus lokaal bijwerken: geen volledige herlaad nodig. */
  onShareChanged(change: HdChartShareChange): void {
    this.items.update(list => list.map(item =>
      item.id === change.id ? { ...item, shareToken: change.shareToken, isShared: change.shareToken != null } : item));
  }

  /** Snelle actie: de als "My chart" gemarkeerde chart direct inladen. */
  protected async loadMyChartAction(): Promise<void> {
    this.quickActionMessage.set('');
    try {
      const chart = await this.groupService.getMyChart();
      if (!chart?.id) {
        this.quickActionMessage.set(this.text().noMyChart);
        return;
      }
      await this.loadChart(chart.id);
    } catch (e) {
      this.quickActionMessage.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  // ── Relaties (fase 5) ─────────────────────────────────────────────────
  protected openRelations(item: PlatformChartListItemDto): void {
    if (!item.id) return;
    this.relationsTarget.set({ chartId: item.id, chartName: item.name ?? '' });
  }

  /** Na een wijziging in de dialog is ook de cache van de tegenpartij verouderd → alles weg. */
  protected onRelationsChanged(chartId: string): void {
    this.relationsByChart.set({});
    void this.loadRelations(chartId);
  }

  /** Relaties voor de detail-pane-chips; undefined zolang niet geladen. */
  protected chartRelations(item: PlatformChartListItemDto): PlatformChartRelationDto[] | undefined {
    return item.id ? this.relationsByChart()[item.id] : undefined;
  }

  protected relationIcon(relationType: string | null | undefined): string {
    return hdRelationTypeIcon(relationType);
  }

  protected relationLabel(relationType: string | null | undefined): string {
    return hdRelationTypeLabel(this.contentTranslate(), relationType);
  }

  private async loadRelations(id: string): Promise<void> {
    if (this.relationsByChart()[id]) return;
    const relations = await this.groupService.listRelations(id);
    this.relationsByChart.update(map => ({ ...map, [id]: relations }));
  }

  /** Detail lazy laden zodra een rij voor het eerst wordt uitgeklapt. */
  protected onRowExpandedChange(event: TableRowExpandedChange<PlatformChartListItemDto>): void {
    if (!event.expanded || !event.item.id) return;
    void this.onRowExpanded(event.item.id);
  }

  onPageChange(page: number): void {
    // Filter/sort resetten zelf al naar pagina 1; een identieke emit overslaan voorkomt dubbele requests.
    if (page === this.page()) return;
    this.page.set(page);
    void this.reload();
  }

  /** Paginagrootte-keuze uit de pager; de tabel springt zelf al terug naar pagina 1. */
  protected onPageSizeChange(size: number): void {
    if (size === this.pageSize()) return;
    this.pageSize.set(size);
    this.page.set(1);
    void this.reload();
  }

  private searchDebounce: ReturnType<typeof setTimeout> | undefined;

  /** Autosearch: elke toetsaanslag herstart de debounce; na 300 ms rust zoeken. */
  protected onSearchModelChange(value: string): void {
    this.searchModel = value;
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.onSearch(value), 300);
  }

  onSearch(text: string): void {
    clearTimeout(this.searchDebounce);
    if (text === this.search()) return; // ongewijzigd (bv. debounce + knop-klik) → geen extra call
    this.search.set(text);
    this.page.set(1);
    void this.reload();
  }

  onSort(sortFields: SortField<PlatformChartListItemDto>[]): void {
    this.sortFields.set(sortFields);
    this.page.set(1);
    void this.reload();
  }

  onFilter(filterFields: FilterField<PlatformChartListItemDto>[]): void {
    this.filterFields.set(filterFields);
    this.page.set(1);
    void this.reload();
  }

  async onRowExpanded(id: string): Promise<void> {
    if (!id || this.details()[id]) return;
    const detail = await this.library.getChartDocument(id);
    this.details.update(map => ({ ...map, [id]: detail }));
    void this.loadGroupNames(id);
    void this.loadRelations(id);
  }

  private onRemoteChartChange(change: RemoteChartChange): void {
    const documentChanged = change.kinds.includes('document') || change.kinds.includes('notes');
    for (const id of change.chartIds) {
      if (documentChanged && this.details()[id]) {
        this.library.getChartDocument(id)
          .then(detail => this.details.update(map => ({ ...map, [id]: detail })))
          .catch(() => undefined);
      }
      if (change.kinds.includes('relations') && this.relationsByChart()[id]) {
        this.relationsByChart.update(map => {
          const rest = { ...map };
          delete rest[id];
          return rest;
        });
        void this.loadRelations(id);
      }
    }
    clearTimeout(this.remoteReloadTimer);
    this.remoteReloadTimer = setTimeout(() => {
      void this.reload();
      void this.reloadFacets();
    }, 500);
  }

  private async loadGroupNames(id: string): Promise<void> {
    if (this.groupNamesByChart()[id]) return;
    const groupIds = await this.groupService.getChartGroupIds(id);
    const nameById = new Map(this.groups().map(group => [group.id, group.name] as const));
    const names = groupIds
      .map(groupId => nameById.get(groupId))
      .filter((name): name is string => !!name);
    this.groupNamesByChart.update(map => ({ ...map, [id]: names }));
  }

  async loadChart(id: string): Promise<void> {
    const { meta, preset } = await this.library.getChartDocument(id);
    this.loadChartRequested.emit({ id, preset, type: meta.type ?? PlatformChartType.Standard });
  }

  async deleteChart(item: PlatformChartListItemDto): Promise<void> {
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.confirmDelete',
      { name: item.name },
      this.contentLang.effectiveLang(),
    );
    const ok = await this.confirmer.confirm(message, { color: 'danger' });
    if (!ok || !item.id) return;
    await this.library.deleteChart(item.id);
    this.chartsDeleted.emit([item.id]);
    await this.reload();
  }

  /** Geboortetijdstip is een UTC-moment; toon het als zodanig (geen browser-tijdzone op toepassen). */
  /** Geboortemoment als kloktijd op de geboorteplaats; UTC enkel als terugval (oude rij). */
  protected formatBirthDate(item: PlatformChartListItemDto): string {
    if (item.birthDateLocal) {
      const local = moment(item.birthDateLocal);
      if (local.isValid()) return local.format(DATE_TIME_FORMAT);
    }
    if (!item.birthDateUtc) return '—';
    const parsed = moment.utc(item.birthDateUtc);
    return parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : '—';
  }

  /** Audit-tijdstippen (aangemaakt/gewijzigd) in lokale tijd. */
  protected formatTimestamp(timestamp: string | null | undefined): string {
    if (!timestamp) return '—';
    const parsed = moment(timestamp);
    return parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : '—';
  }

  protected typeIcon(value: PlatformChartType): string {
    return HD_CHART_TYPE_ICONS[value] ?? HD_CHART_TYPE_ICONS[PlatformChartType.Unknown];
  }

  protected readonly PlatformChartType = PlatformChartType;

  private contentTranslate(): (key: string) => string {
    const language = this.contentLang.effectiveLang();
    return (key: string) => this.transloco.translate<string>(key, undefined, language);
  }

  /** Éénregelige HD-samenvatting uit de gepromoveerde list-DTO-velden; '—' zolang niet gestampt. */
  protected hdSummary(item: PlatformChartListItemDto): string {
    return hdSummaryText(this.contentTranslate(), item) ?? '—';
  }

  protected energyTypeIcon(item: PlatformChartListItemDto): string | null {
    return item.hdType ? HD_ENERGY_TYPE_ICONS[item.hdType] ?? null : null;
  }

  protected energyTypeLabel(item: PlatformChartListItemDto): string {
    return hdTypeLabel(this.contentTranslate(), item.hdType) ?? '—';
  }

  protected authorityLabel(item: PlatformChartListItemDto): string {
    return hdAuthorityLabel(this.contentTranslate(), item.hdAuthority) ?? '—';
  }

  protected definitionLabel(item: PlatformChartListItemDto): string {
    return hdDefinitionLabel(this.contentTranslate(), item.hdDefinition) ?? '—';
  }

  // ── Backfill: alle charts herstampen (B8 — bumpt LastModifiedOn, bevestigd via confirm) ──
  protected readonly backfillRunning = signal(false);
  protected readonly backfillStatus = signal('');

  protected async runBackfill(): Promise<void> {
    const language = this.contentLang.effectiveLang();
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(key, params, language);

    const ok = await this.confirmer.confirm(translate('humanDesign.chartManager.backfill.confirm'));
    if (!ok) return;

    this.backfillRunning.set(true);
    let done = 0;
    let failed = 0;
    try {
      // Eerst alle ids verzamelen (stabiel t.o.v. de LastModifiedOn-bumps tijdens het herstampen).
      const ids: string[] = [];
      let page = 1;
      for (;;) {
        const result = await this.library.queryCharts(buildChartDataSourceQuery({ page, pageSize: 100 }));
        ids.push(...result.items.map(item => item.id).filter((id): id is string => !!id));
        if (result.items.length === 0 || page >= result.totalPageCount) break;
        page++;
      }

      for (const id of ids) {
        try {
          const { preset } = await this.library.getChartDocument(id);
          await this.library.replaceDocument(id, preset);
          // Herstampt document → open tabs van deze chart verversen (de host
          // fetcht alleen voor charts die daadwerkelijk open staan).
          this.chartUpdated.emit(id);
        } catch {
          failed++;
        }
        done++;
        this.backfillStatus.set(translate('humanDesign.chartManager.backfill.progress', { done, total: ids.length }));
      }

      this.backfillStatus.set(translate('humanDesign.chartManager.backfill.result', { done: done - failed, failed }));
    } finally {
      this.backfillRunning.set(false);
      this.details.set({});
      await this.reload();
    }
  }

  openCreate(): void { this.createDialogOpen.set(true); }

  async openEdit(item: PlatformChartListItemDto): Promise<void> {
    if (!item.id) return;
    this.editTargetIsMyChart.set(item.isMyChart ?? false);
    this.editTarget.set(await this.library.getChartDocument(item.id));
  }

  /** Bewerkte chart persisteren; detail-cache van die rij invalideren zodat de reload verse data prefetcht. */
  async onChartSaved(input: HdChartUpdateInput): Promise<void> {
    await this.library.updateChart(input);
    // Open tabs in de chart-omgeving tonen anders het oude document uit hun snapshot.
    this.chartUpdated.emit(input.id);
    // "My chart"-checkbox alleen persisteren bij een echte wijziging (aparte API-call).
    const desiredMyChart = input.isMyChart ?? false;
    const currentMyChart = this.items().find(item => item.id === input.id)?.isMyChart ?? false;
    if (desiredMyChart !== currentMyChart) {
      await this.groupService.setMyChart(input.id, desiredMyChart);
    }
    this.details.update(map => {
      const rest = { ...map };
      delete rest[input.id];
      return rest;
    });
    await this.reload();
  }

  /** Manager-context: berekende chart opslaan ná een duplicaatcontrole (niet-blokkerende confirm). */
  async onChartCalculated(result: HdChartCalculationResult): Promise<void> {
    // "Enkel berekenen en niet opslaan": niets persisteren — de host (dialog-modus)
    // opent de chart als onopgeslagen tab, zoals de aanmaak-dialog op de omgevingspagina.
    if (result.calculateOnly) {
      this.calculatedChartRequested.emit(result);
      return;
    }
    if (!(await this.confirmNoDuplicates(result))) return;
    // Gedeelde opslag-kern met de omgevingspagina: type uit het document, naam in
    // het document, bronrelaties. Alleen de context (duplicaatcheck, lijst-reload)
    // is manager-specifiek.
    const { chart, relationsWarning } = await this.library.createCalculatedChart({
      preset: result.preset,
      name: result.name,
      type: chartTypeForPreset(result.preset),
    });
    if (relationsWarning) {
      this.notifier.error(relationsWarning);
    }
    if (chart.id && result.markAsMyChart) {
      await this.groupService.setMyChart(chart.id, true);
    }
    await this.reload();
    void this.reloadFacets();
  }

  /** true = doorgaan (geen duplicaten, checkfout, of gebruiker bevestigde "toch aanmaken"). */
  private async confirmNoDuplicates(result: HdChartCalculationResult): Promise<boolean> {
    let duplicates: PlatformChartListItemDto[];
    try {
      // Bij afgeleide charts (composiet/cycle/snapshot) is birthData de natale
      // snapshot van de bronpersoon: op geboortemoment matchen zou elke van die
      // persoon afgeleide chart aanwijzen. Alleen de (auto-)naam is daar een
      // zinvolle duplicaatsleutel.
      const birthUtc = chartTypeForPreset(result.preset) !== PlatformChartType.Standard
        ? null
        : (result.preset?.birthData as { time?: { utc?: string } } | undefined)?.time?.utc ?? null;
      duplicates = await this.groupService.checkDuplicates(result.name ?? null, birthUtc);
    } catch {
      return true; // check-fout mag het aanmaken nooit blokkeren
    }
    if (duplicates.length === 0) return true;

    const names = duplicates.map(duplicate => duplicate.name).filter(Boolean).join(', ');
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.duplicates.confirm',
      { names },
      this.contentLang.effectiveLang(),
    );
    return this.confirmer.confirm(message, { color: 'danger' });
  }
}
